import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useResearchCapability } from "@/hooks/useResearchCapability";
import { toast } from "sonner";
import { format } from "date-fns";

type Task = {
  id: string;
  name: string;
  task_kind: string;
  judge_model: string | null;
  is_active: boolean;
  notes: string | null;
  input: any;
  expected: any;
  rubric: any;
  created_by: string | null;
  created_at: string;
};

export default function ResearchEvals() {
  const { data: canManage, isLoading: capLoading } = useResearchCapability("manage");
  const [params] = useSearchParams();
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [selected, setSelected] = useState<Task | null>(null);
  const qc = useQueryClient();

  const { data: tasks, isLoading } = useQuery({
    queryKey: ["research_eval_tasks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_eval_tasks")
        .select("*")
        .order("name");
      if (error) throw error;
      return data as Task[];
    },
    enabled: !!canManage,
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from("research_eval_tasks").update({ is_active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["research_eval_tasks"] });
      toast.success("Updated");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const kinds = useMemo(() => [...new Set((tasks ?? []).map((t) => t.task_kind))].sort(), [tasks]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (tasks ?? []).filter((t) => {
      if (kindFilter && t.task_kind !== kindFilter) return false;
      if (activeOnly && !t.is_active) return false;
      if (q && !t.name.toLowerCase().includes(q) && !(t.notes ?? "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [tasks, search, kindFilter, activeOnly]);

  if (capLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!canManage) return <p className="text-sm text-muted-foreground">Requires manage capability.</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Eval tasks</h2>
          <p className="text-sm text-muted-foreground">{tasks?.length ?? 0} tasks seeded</p>
        </div>
        <Button asChild variant="outline">
          <Link to="/partner/research/evals/runs">View runs →</Link>
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Input
          placeholder="Search name or notes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <select
          className="h-10 px-3 rounded-md border border-input bg-background text-sm"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
        >
          <option value="">All kinds</option>
          {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={activeOnly} onCheckedChange={setActiveOnly} />
          Active only
        </label>
        <div className="text-xs text-muted-foreground ml-auto">{filtered.length} shown</div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="text-sm text-muted-foreground p-4">Loading tasks…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Judge model</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((t) => (
                  <TableRow key={t.id} className="cursor-pointer" onClick={() => setSelected(t)}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell><Badge variant="outline">{t.task_kind}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{t.judge_model ?? "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-md truncate">{t.notes ?? "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {format(new Date(t.created_at), "dd/MM/yyyy")}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={t.is_active}
                        onCheckedChange={(v) => toggleActive.mutate({ id: t.id, is_active: v })}
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toast.info("Eval execution is a Phase 2 feature. The eval harness backend deploys then.")}
                      >
                        Run
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {selected?.name}
              {selected && <Badge variant="outline">{selected.task_kind}</Badge>}
            </SheetTitle>
          </SheetHeader>
          {selected && (
            <div className="space-y-3 mt-4 text-sm">
              {selected.notes && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Notes</div>
                  <p className="text-sm">{selected.notes}</p>
                </div>
              )}
              {selected.judge_model && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Judge model</div>
                  <code className="text-xs">{selected.judge_model}</code>
                </div>
              )}
              <JsonBlock label="Input" value={selected.input} />
              <JsonBlock label="Expected" value={selected.expected} />
              <JsonBlock label="Rubric" value={selected.rubric} />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function JsonBlock({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{label}</div>
      <pre className="text-xs bg-muted/40 p-3 rounded overflow-x-auto">
        {value ? JSON.stringify(value, null, 2) : "—"}
      </pre>
    </div>
  );
}
